import React, { FormEventHandler, useCallback, useRef, useState } from 'react'
import CrosshairLink from '../crosshair-link'
import * as styles from '../../styles/components/content-blocks/contact-form.module.scss'
import cx from 'classnames'
import { navigate } from 'gatsby'
import Heading from '../heading'
import Border from '../border'
import ReCAPTCHA from 'react-google-recaptcha'
import { useSiteMetadata } from '../../hooks/use-site-metadata'
import { motion, useScroll, useTransform } from 'framer-motion'
import { bezierEaseIn } from '../../utils/styles'
import Dropdown, { DropdownOption } from '../dropdown'

export interface ContactFormProps {
    title?: string
    borderBottom?: boolean
    preselectedTopic?: string
}

interface State {
    submitting: boolean
    success: boolean | null
    verificationToken: string
}

interface FormFieldProps {
    type: React.HTMLProps<HTMLInputElement>['type']
    name: string
    label: string
}

const contactTopics: DropdownOption[] = [
    { id: 'shm', label: 'Structural Health Monitoring' },
    { id: 'worksites', label: 'Worksites' },
    { id: 'machine-automation', label: 'Machine Automation' },
    { id: 'other', label: 'Other' },
]

const FormField = ({ type, name, label }: FormFieldProps) => {
    return (
        <div className={styles.formField}>
            <label className={styles.formLabel}>
                {label}
                <input
                    type={type}
                    name={name}
                    placeholder="..."
                    required
                    className={styles.formInput}
                />
            </label>
        </div>
    )
}

const submitResponseToServer = (
    endpointUrl: string, form: HTMLFormElement, reCaptchaToken: string,
): Promise<Response> => {
    const body = new FormData(form)
    body.append('g-recaptcha-response', reCaptchaToken)

    return fetch(endpointUrl, {
        method: 'post',
        body: new FormData(form),
        headers: {
            'Accept': 'application/json',
        },
    })
}

const ContactForm = ({ title, borderBottom, preselectedTopic }: ContactFormProps ) => {
    const [formState, setFormState] = useState<State>({
        submitting: false,
        success: null,
        verificationToken: '',
    })
    const [reCaptchaLoaded, setReCaptchaLoaded] = useState<boolean>(false)
    const { reCaptchaSiteKey, contactFormEndpoint } = useSiteMetadata()
    const recaptcha = useRef<ReCAPTCHA>(null)

    const handleServerResponse = useCallback((success: boolean, form: HTMLFormElement) => {
        setFormState((state) => ({ ...state, submitting: false, success }))

        if (!success) {
            return window.alert('There was an error sending your message. Please try again later.')
        }

        form.reset()
        void navigate('/thank-you-x4jf7s')
    }, [])

    const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(async (event) => {
        setFormState((state) => ({ ...state, submitting: true }))
        event.preventDefault()
        const form = event.target as HTMLFormElement

        if (!recaptcha.current) {
            return
        }

        const reCaptchaToken = await recaptcha.current.executeAsync()

        if (!reCaptchaToken) {
            return
        }

        submitResponseToServer(contactFormEndpoint, form, reCaptchaToken)
            .then((res) => {
                handleServerResponse(res.ok, form)
            })
            .catch((err) => handleServerResponse(false, form))
    }, [handleServerResponse])

    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0.1, 0.7], [0, 1], { ease: bezierEaseIn })

    return (
        <>
            <div
                ref={targetRef} className={styles.container}
            >
                {title &&
                    <motion.span style={{ opacity }}>
                        <Heading className={styles.title}>
                            {title}
                        </Heading>
                    </motion.span>
                }
                <form onSubmit={handleSubmit} name="universal-contact-form" className={styles.form}>
                    <motion.div style={{ opacity }} className={styles.formFieldsContainer}>
                        <FormField type="text" name="company" label="Company" />
                        <FormField type="text" name="name" label="Name" />
                        <FormField type="email" name="email" label="Email" />
                        <div className={styles.formField}>
                            <label className={styles.formLabel}>
                                I am interested in
                                <Dropdown
                                    name="topic"
                                    options={contactTopics}
                                    preselectedOptionId={preselectedTopic}
                                    className={styles.formInput}
                                />
                            </label>
                        </div>
                        <HoneyPot />
                        <div className={styles.formField}>
                            <label className={styles.formLabel}>
                            Message
                                <textarea
                                    name="message"
                                    placeholder="..."
                                    required
                                    className={cx(styles.formInput, styles.textArea)}
                                />
                            </label>
                        </div>
                    </motion.div>
                    <div className={styles.buttonContainer}>
                        <CrosshairLink
                            label="Send message"
                            as={'button'}
                            type={'submit'}
                            colorVariant={'dark'}
                            disabled={formState.submitting || !reCaptchaLoaded}
                            className={styles.submitButton}
                        />
                        <ReCAPTCHA
                            ref={recaptcha}
                            sitekey={reCaptchaSiteKey}
                            size="invisible"
                            badge="inline"
                            style={{ display: 'inline-block' }}
                            asyncScriptOnLoad={() => setReCaptchaLoaded(true)}
                        />
                    </div>
                </form>
            </div>
            {borderBottom && <Border style={{ opacity }} />}
        </>
    )
}

const HoneyPot = () => {
    // A bait form input which, if not empty, marks the submission as spam
    return (
        <input
            type="text"
            name="_details"
            tabIndex={-1}
            aria-hidden
            style={{
                position: 'fixed',
                left: '1000vw',
            }}
        />

    )
}

export default ContactForm
